import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  CircleDashed,
  DollarSign,
  Inbox,
  Loader2,
  Plus,
  SlidersHorizontal,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  ManagementBulkActionBar,
  ManagementCardGrid,
  ManagementEmptyState,
  ManagementErrorState,
  ManagementFlagBadge,
  ManagementHeader,
  ManagementMetricGrid,
  ManagementPage,
  ManagementPriorityBadge,
  ManagementRecordCard,
  ManagementStatusBadge,
  ManagementTable,
  ManagementTableSurface,
  ManagementToolbar,
  type ManagementActiveFilter,
  type ManagementFilterGroup,
  type ManagementMetric,
  type ManagementViewMode,
} from '@/components/management';

const metricSamples: ManagementMetric[] = [
  {
    id: 'today',
    label: 'Today',
    value: 18,
    hint: 'Due, overdue, urgent, or in motion',
    icon: CircleDashed,
    active: true,
  },
  {
    id: 'overdue',
    label: 'Overdue',
    value: 3,
    hint: 'Past target date',
    icon: AlertTriangle,
    tone: 'destructive',
  },
  {
    id: 'ready-to-bill',
    label: 'Ready to Bill',
    value: 9,
    hint: 'Tracked time waiting on invoice',
    icon: DollarSign,
    tone: 'success',
  },
  {
    id: 'done-this-week',
    label: 'Done This Week',
    value: 27,
    hint: 'Closed in the last 7 days',
    icon: CheckCircle2,
  },
];

const rows = [
  {
    id: 'tsk_101',
    task: 'Finalize homepage launch QA notes',
    client: 'Northstar Dental',
    initials: 'N',
    status: 'In Progress',
    priority: 'High',
    estimate: '2h',
    tracked: '1h 20m',
    due: 'Jun 24',
    flags: ['Send To Client', 'Ready'],
  },
  {
    id: 'tsk_102',
    task: 'Draft billing closeout for March hosting',
    client: 'Pine Harbor',
    initials: 'P',
    status: 'Open',
    priority: 'Normal',
    estimate: '45m',
    tracked: '0m',
    due: 'Jun 25',
    flags: ['Ready to Bill'],
  },
  {
    id: 'tsk_103',
    task: 'Review proposal automation retry logs',
    client: 'One Up',
    initials: '1U',
    status: 'Waiting',
    priority: 'Low',
    estimate: '1h',
    tracked: '35m',
    due: 'Jun 26',
    flags: ['Internal'],
  },
];

const queueOptions = [
  { value: 'today', label: 'Today', count: 18 },
  { value: 'overdue', label: 'Overdue', count: 3 },
  { value: 'ready_to_bill', label: 'Ready to Bill', count: 9 },
  { value: 'done_this_week', label: 'Done This Week', count: 27 },
];

export function ManagementUiKitView() {
  const [viewMode, setViewMode] = useState<ManagementViewMode>('table');
  const [queue, setQueue] = useState('today');
  const [search, setSearch] = useState('');

  const filters = useMemo<ManagementFilterGroup[]>(
    () => [
      {
        id: 'queue',
        label: 'Queue',
        value: queue,
        onChange: setQueue,
        options: queueOptions,
      },
    ],
    [queue],
  );

  const activeFilters = useMemo<ManagementActiveFilter[]>(
    () => [
      {
        id: 'queue',
        label: 'Queue',
        value: queueOptions.find((option) => option.value === queue)?.label,
        onClear: () => setQueue('today'),
      },
      ...(search
        ? [
            {
              id: 'search',
              label: 'Search',
              value: `"${search}"`,
              onClear: () => setSearch(''),
            },
          ]
        : []),
    ],
    [queue, search],
  );

  return (
    <ManagementPage>
      <ManagementHeader
        title="Tasks"
        description={
          <span className="flex flex-wrap gap-x-2 gap-y-1">
            <span className="font-medium text-foreground">Daily operating queue.</span>
            <span>Open and In Progress tasks, most recently modified first.</span>
          </span>
        }
        source={{ system: 'Airtable', label: 'ENT_Projects' }}
        metrics={metricSamples}
        showMetrics
        filters={filters}
        activeFilters={activeFilters}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: 'Search task, notes, or client',
        }}
        secondaryActions={[
          { label: 'More', icon: SlidersHorizontal, variant: 'outline', onClick: () => undefined },
        ]}
        primaryAction={{ label: 'Create Task', icon: Plus, onClick: () => undefined }}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      <SectionTitle title="Buttons and Badges" />
      <ManagementTableSurface className="p-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" className="h-8 gap-1.5 px-3.5">
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Create Task
          </Button>
          <Button size="sm" variant="outline" className="h-8">
            Secondary
          </Button>
          <Button size="sm" variant="ghost" className="h-8">
            Ghost
          </Button>
          <ManagementStatusBadge variant="info">In Progress</ManagementStatusBadge>
          <ManagementStatusBadge variant="success">Closed</ManagementStatusBadge>
          <ManagementPriorityBadge variant="destructive">Urgent</ManagementPriorityBadge>
          <ManagementPriorityBadge variant="warning">High</ManagementPriorityBadge>
          <ManagementFlagBadge>Send To Client</ManagementFlagBadge>
        </div>
      </ManagementTableSurface>

      <SectionTitle title="Toolbar and Bulk Actions" />
      <ManagementToolbar
        filters={filters}
        activeFilters={activeFilters}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: 'Search records',
        }}
      />
      <ManagementBulkActionBar selectedCount={2} onClear={() => undefined}>
        <Button type="button" size="sm" variant="outline" className="h-8">
          Send To Client
        </Button>
        <Button type="button" size="sm" variant="outline" className="h-8">
          Mark Closed
        </Button>
      </ManagementBulkActionBar>

      <SectionTitle title="Metric Cards" />
      <ManagementMetricGrid metrics={metricSamples} />

      <SectionTitle title="Table Surface" />
      <ManagementTable>
        <table className="min-w-[920px]">
          <thead>
            <tr>
              <th className="w-10">
                <input type="checkbox" aria-label="Select all demo rows" className="accent-primary" />
              </th>
              <th>Task</th>
              <th>Client</th>
              <th>Status</th>
              <th>Priority</th>
              <th>Estimate</th>
              <th>Tracked</th>
              <th>Due</th>
              <th>Flags</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.id} className={index === 0 ? 'bg-primary/[0.025]' : undefined}>
                <td>
                  <input type="checkbox" checked={index === 0} readOnly className="accent-primary" />
                </td>
                <td>
                  <div className="font-semibold text-foreground">{row.task}</div>
                  <div className="mt-0.5 text-[11px] text-muted-foreground">Updated 18m ago</div>
                </td>
                <td>
                  <div className="flex items-center gap-2">
                    <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-[10px] font-semibold text-primary">
                      {row.initials}
                    </span>
                    <span className="text-muted-foreground">{row.client}</span>
                  </div>
                </td>
                <td>
                  <ManagementStatusBadge variant={row.status === 'In Progress' ? 'info' : 'muted'}>
                    {row.status}
                  </ManagementStatusBadge>
                </td>
                <td>
                  <ManagementPriorityBadge
                    variant={
                      row.priority === 'High'
                        ? 'warning'
                        : row.priority === 'Low'
                          ? 'muted'
                          : 'default'
                    }
                  >
                    {row.priority}
                  </ManagementPriorityBadge>
                </td>
                <td className="text-muted-foreground">{row.estimate}</td>
                <td className="text-muted-foreground">{row.tracked}</td>
                <td className="text-muted-foreground">{row.due}</td>
                <td>
                  <div className="flex flex-wrap gap-1">
                    {row.flags.map((flag) => (
                      <ManagementFlagBadge key={flag}>{flag}</ManagementFlagBadge>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ManagementTable>

      <SectionTitle title="Card View" />
      <ManagementCardGrid>
        {rows.map((row, index) => (
          <ManagementRecordCard key={row.id} selected={index === 0}>
            <div className="space-y-2">
              <div>
                <div className="text-sm font-semibold text-foreground">{row.task}</div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  <ManagementStatusBadge variant={row.status === 'In Progress' ? 'info' : 'muted'}>
                    {row.status}
                  </ManagementStatusBadge>
                  <ManagementPriorityBadge variant={row.priority === 'High' ? 'warning' : 'muted'}>
                    {row.priority}
                  </ManagementPriorityBadge>
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 text-[12px] text-muted-foreground">
                <span>{row.client}</span>
                <span>{row.due}</span>
              </div>
            </div>
          </ManagementRecordCard>
        ))}
      </ManagementCardGrid>

      <SectionTitle title="Empty, Loading, Error" />
      <div className="grid gap-2.5 lg:grid-cols-3">
        <ManagementTableSurface>
          <ManagementEmptyState
            icon={Inbox}
            title="No records in this queue."
            body="Clear filters or change the queue to continue."
            action={
              <Button size="sm" variant="outline" className="mt-1 h-8">
                Clear filters
              </Button>
            }
          />
        </ManagementTableSurface>
        <ManagementTableSurface className="flex min-h-[190px] items-center justify-center">
          <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Loading records
          </div>
        </ManagementTableSurface>
        <ManagementErrorState title="Could not load records" error={new Error('Worker request failed')} />
      </div>
    </ManagementPage>
  );
}

function SectionTitle({ title }: { title: string }) {
  return (
    <div className="pt-2 text-[11px] font-semibold uppercase tracking-[0.55px] text-muted-foreground">
      {title}
    </div>
  );
}
